import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/common/_reducers';
import { Farm, FarmGroup } from 'src/app/common/_models';
import * as ProductActions from 'src/app/common/_actions/product';
import * as FarmActions from 'src/app/common/_actions/farm';
import { selectCurrentFarm } from 'src/app/common/_selectors/currentFarm.selectors';
import { ThemeConfig } from 'src/assets/data/enum/themeConfig';
import { SAASBRANDNAME } from 'src/assets/data/enum/saasBrandName.enum';

@Injectable()
export class FarmService {
  private apiBaseUrl = environment.apiBaseUrl;
  public list = new BehaviorSubject<Farm[]>([]);
  public me = new BehaviorSubject<Farm>(null);
  currentFarmParam;
  farmMe$: Observable<Farm>;
  preferredProductsCountry$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  preferredCurrency$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  selectedFarmGroup$: BehaviorSubject<FarmGroup> = new BehaviorSubject<FarmGroup>(null);
  private googleMapsApiLoader: Loader;
  themeConfig = ThemeConfig;
  brand = SAASBRANDNAME;

  constructor(
    private http: HttpClient,
    @Inject('googleMapsApiKey') private apiKey: string,
    @Inject('googleMapsLibraries') private libraries: any,
    private store: Store<AppState>
  ) {
    this.farmMe$ = this.store.select(selectCurrentFarm);
    this.store.select(selectCurrentFarm).subscribe((currentFarm) => {
      if (currentFarm && currentFarm.id) {
        if (currentFarm.preferredProductsCountry) {
          this.setPreferredProductsCountry(currentFarm.preferredProductsCountry);
        } else {
          this.setPreferredProductsCountry('ITA');
        }
        this.store.dispatch(ProductActions.loadProducts());

        if (currentFarm.preferredCurrency) {
          this.setPreferredCurrency(currentFarm.preferredCurrency);
        } else {
          this.setPreferredCurrency('EUR');
        }
      }
    });

    // Initialize Google Maps API loader
    this.googleMapsApiLoader = new Loader({
      apiKey: this.apiKey,
      libraries: this.libraries,
    });
  }

  getPreferredProductsCountry() {
    return this.preferredProductsCountry$.asObservable();
  }

  setPreferredProductsCountry(value) {
    this.preferredProductsCountry$.next(value);
  }

  getPreferredCurrency() {
    return this.preferredCurrency$.asObservable();
  }

  setPreferredCurrency(value) {
    this.preferredCurrency$.next(value);
  }

  getFarmMeFromLocalStore(): Observable<Farm> {
    return this.farmMe$;
  }

  getList(): Observable<Farm[]> {
    let excludesAppend = '';
    // ?excludes=fields removed because in hubfarm field is needed during the automatic account switch
    if (this.themeConfig.brand !== this.brand.HUBFARM) {
      excludesAppend = '?excludes=fields';
    }
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v1/farms' + excludesAppend).pipe(
      map((response) => {
        if (response) {
          this.list.next(response);
          return response;
        }
        return null;
      })
    );
  }

  getCurrentFarm(forceReload = false): Observable<Farm> {
    let farm: Farm = JSON.parse(localStorage.getItem('companySelected'));
    if (forceReload || !farm || !farm.id || !('welcomeCheck' in farm)) {
      if (!farm || !farm.id) {
        farm = { id: 'me' } as any;
      }
      return this.http.get<any>(this.apiBaseUrl + 'api/private/v1/farms/' + farm.id).pipe(
        map((response) => {
          if (response) {
            const farm = response;
            localStorage.setItem('companySelected', JSON.stringify(farm));
            this.selectedFarmGroup$.next(farm.groups && farm.groups.length ? farm.groups[0] : null);
            return farm;
          }

          return null;
        })
      );
    } else {
      return new Observable((observer) => {
        observer.next(farm);
        observer.complete();
      });
    }
  }

  getLocalCurrentFarm = (): Farm => {
    if (localStorage.getItem('companySelected')) {
      return JSON.parse(localStorage.getItem('companySelected'));
    } else {
      this.getCurrentFarm().subscribe((response) => response);
    }

    return null;
  };

  saveFarm(farm): Observable<Farm> {
    const farmCopy = { ...farm };

    delete farmCopy.lastUpdated;
    delete farmCopy.dateCreated;

    if (farmCopy.devices) {
      const arrdevices: Array<any> = [];
      farmCopy.devices.forEach((element) => {
        const iddevice: any = {
          id: element.id,
        };
        arrdevices.push(iddevice);
      });
      farmCopy.devices = arrdevices;
    }

    if (farmCopy.fields) {
      const arrfields: Array<any> = [];
      farmCopy.fields.forEach((element) => {
        const idfield: any = {
          id: element.id,
        };
        arrfields.push(idfield);
      });
      farmCopy.fields = arrfields;
    }

    if (farmCopy.sheets) {
      const arrsheets: Array<any> = [];
      farmCopy.sheets.forEach((element) => {
        const idsheet: any = {
          id: element.id,
        };
        arrsheets.push(idsheet);
      });
      farmCopy.sheets = arrsheets;
    }

    if (farmCopy.users) {
      const arrusers: Array<any> = [];
      farmCopy.users.forEach((element) => {
        const iduser: any = {
          id: element.id,
        };
        arrusers.push(iduser);
      });
      farmCopy.users = arrusers;
    }

    if (farmCopy.id) {
      delete farmCopy.users;
      delete farmCopy.sheets;
      delete farmCopy.devices;

      return this.http
        .put<Farm>(this.apiBaseUrl + 'api/private/v1/farms/' + farmCopy.id, farmCopy)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<Farm>(this.apiBaseUrl + 'api/private/v1/farms/', farmCopy)
        .pipe(map((response) => response));
    }
  }

  deleteFarm(id: number): Observable<Farm> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/farms/' + id).pipe(
      map((response) => {
        if (response) {
          return { id } as Farm;
        }

        return null;
      })
    );
  }

  saveFarmWithCity(farm: Farm, meteo: boolean) {
    this.googleMapsApiLoader.load().then(() => {
      new google.maps.Geocoder().geocode(
        {
          location: new google.maps.LatLng(
            farm.meteoLat ? farm.meteoLat : farm.lat,
            farm.meteoLng ? farm.meteoLng : farm.lng
          ),
          language: 'it',
        },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK) {
            if (results[1]) {
              let country = null,
                countryCode = null,
                city = null,
                cityAlt = null;
              let c, lc, component;
              for (let r = 0, rl = results.length; r < rl; r += 1) {
                const result = results[r];
                if (!city && result.types[0] === 'locality') {
                  for (c = 0, lc = result.address_components.length; c < lc; c += 1) {
                    component = result.address_components[c];
                    if (component.types[0] === 'locality') {
                      city = component.long_name;
                      break;
                    }
                  }
                } else if (!city && !cityAlt && result.types[0] === 'administrative_area_level_3') {
                  for (c = 0, lc = result.address_components.length; c < lc; c += 1) {
                    component = result.address_components[c];
                    if (component.types[0] === 'administrative_area_level_3') {
                      cityAlt = component.long_name;
                      break;
                    }
                  }
                } else if (!city && !cityAlt && result.types[0] === 'administrative_area_level_2') {
                  for (c = 0, lc = result.address_components.length; c < lc; c += 1) {
                    component = result.address_components[c];
                    if (component.types[0] === 'administrative_area_level_2') {
                      cityAlt = component.long_name;
                      break;
                    }
                  }
                } else if (!city && !cityAlt && result.types[0] === 'route') {
                  if (result.address_components.length < 3) {
                    component = result.address_components[0];
                  } else {
                    component = result.address_components[1];
                  }
                  cityAlt = component.long_name;
                } else if (!country && result.types[0] === 'country') {
                  country = result.address_components[0].long_name;
                  countryCode = result.address_components[0].short_name;
                }
                if (city && country) {
                  break;
                }
              }
              const countrySave = country;
              const citySave = city ? city : cityAlt;

              if (!meteo) {
                farm.meteoLat = farm.lat;
                farm.meteoLng = farm.lng;
              }
              farm.meteoAsl = farm.asl;
              farm.meteoCity = citySave;
              farm.meteoCountry = countrySave;
              farm.countryCode = countryCode;
              this.store.dispatch(FarmActions.updateFarm({ farm }));
            }
          }
        }
      );
    });
  }

  setFarmParamByID(id: number, param: any): any {
    this.list.subscribe((listFarm) => {
      if (listFarm && listFarm.length > 0) {
        const singleFarm = listFarm.filter((farm) => farm.id === id);
        if (singleFarm && singleFarm[0]) {
          this.currentFarmParam = singleFarm[0][param];
          return singleFarm[0][param];
        }
      }

      return null;
    });
  }

  getFarmByParam() {
    return this.currentFarmParam;
  }

  getFarmNameByID = (id): any => {
    return this.setFarmParamByID(id, 'name');
  };

  getFarmById = (id: number): Farm => {
    let item: Farm;
    this.list.pipe(first((list: Farm[]) => list && list.length > 0)).subscribe((list: Farm[]) => {
      item = list.find((i) => i.id === id);
    });
    return item;
  };

  syncData() {
    return this.http
      .post<any>(this.apiBaseUrl + 'api/private/v1/company/hubfarm/farm/sync', null)
      .pipe(map((response) => response));
  }

  getFarmGroups = (): FarmGroup[] => {
    return this.getLocalCurrentFarm().groups || [];
  };

  getHasBarillaCustomization = (): boolean => {
    const farmGroup = this.selectedFarmGroup$.getValue();
    const groupName = farmGroup ? farmGroup.group : null;

    return groupName === 'XCHAIN' || groupName === 'CDM' || groupName === 'BARILLA_FARMING';
  };

  setSelectedFarmGroup = (name: string): void => {
    const farm: Farm = this.getLocalCurrentFarm();

    const newGroup: FarmGroup = farm.groups.find((g) => g.group == name);

    if (newGroup) {
      this.selectedFarmGroup$.next(newGroup);
    }
  };
}
